// src/theme/ThemeProvider.js
/**
 * Application Theme Provider
 * Integrates design tokens with Material-UI
 * Provides consistent theming across the entire application
 */

import React, { useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import designTokens from './designTokens';

/**
 * Create MUI theme from design tokens
 */
const createAppTheme = (mode = 'light') => {
  const tokens = designTokens.muiTheme;

  return createTheme({
    palette: {
      mode,
      ...tokens.palette,
    },
    typography: {
      fontFamily: designTokens.typography.fontFamily.base,
      ...tokens.typography,
    },
    shape: tokens.shape,
    shadows: tokens.shadows,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            boxSizing: 'border-box',
          },
          '*': {
            boxSizing: 'inherit',
          },
          '*, *:before, *:after': {
            boxSizing: 'border-box',
          },
          body: {
            margin: 0,
            padding: 0,
            backgroundColor: designTokens.colors.neutral.bgLight,
            color: designTokens.colors.neutral.ink,
            fontFamily: designTokens.typography.fontFamily.base,
            lineHeight: 1.5,
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
          'html, body, #root': {
            height: '100%',
            width: '100%',
          },
          code: {
            fontFamily: designTokens.typography.fontFamily.mono,
          },
          pre: {
            fontFamily: designTokens.typography.fontFamily.mono,
            overflow: 'auto',
            borderRadius: designTokens.borderRadius.md,
            backgroundColor: designTokens.colors.neutral.surface,
            padding: designTokens.spacing.md,
          },
          // Focus visible styles for accessibility
          ':focus-visible': {
            outline: `2px solid ${designTokens.colors.primary.blue}`,
            outlineOffset: '2px',
          },
        },
      },

      MuiButton: {
        defaultProps: {
          disableRipple: false,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: designTokens.borderRadius.button,
            padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
            transition: designTokens.transitions.default,
            '&:focus-visible': {
              outline: `2px solid ${designTokens.colors.primary.blue}`,
              outlineOffset: '2px',
            },
          },
          contained: {
            boxShadow: designTokens.shadows.base,
            '&:hover': {
              boxShadow: designTokens.shadows.hover,
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          },
          outlined: {
            borderColor: designTokens.colors.neutral.border,
            '&:hover': {
              borderColor: designTokens.colors.primary.blue,
              backgroundColor: `rgba(21, 88, 192, ${designTokens.colors.opacity.hover})`,
            },
          },
          text: {
            '&:hover': {
              backgroundColor: `rgba(21, 88, 192, ${designTokens.colors.opacity.hover})`,
            },
          },
          sizeSmall: {
            padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
            fontSize: designTokens.typography.fontSize.sm,
          },
          sizeLarge: {
            padding: `${designTokens.spacing.md} ${designTokens.spacing.lg}`,
            fontSize: designTokens.typography.fontSize.lg,
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.card,
            boxShadow: designTokens.shadows.base,
            border: `1px solid ${designTokens.colors.neutral.border}`,
            transition: designTokens.transitions.shadow,
            '&:hover': {
              boxShadow: designTokens.shadows.hover,
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.input,
            backgroundColor: designTokens.colors.neutral.white,
            transition: designTokens.transitions.default,
            '&:hover': {
              borderColor: designTokens.colors.primary.blue,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px rgba(21, 88, 192, 0.1)`,
              borderColor: designTokens.colors.primary.blue,
            },
            '&.Mui-error': {
              borderColor: designTokens.colors.status.error,
              '&.Mui-focused': {
                boxShadow: `0 0 0 3px rgba(239, 68, 68, 0.1)`,
              },
            },
          },
          notchedOutline: {
            borderColor: designTokens.colors.neutral.border,
          },
        },
      },

      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: designTokens.colors.neutral.white,
            },
          },
        },
      },

      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: designTokens.typography.fontFamily.base,
          },
          input: {
            '&::placeholder': {
              color: designTokens.colors.neutral.muted,
              opacity: 1,
            },
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.md,
            fontWeight: 600,
          },
          filled: {
            backgroundColor: designTokens.colors.primary.lightBlue,
            color: designTokens.colors.neutral.white,
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.md,
            border: `1px solid`,
            boxShadow: designTokens.shadows.subtle,
          },
          standardSuccess: {
            backgroundColor: `rgba(22, 163, 74, ${designTokens.colors.opacity.hover})`,
            borderColor: designTokens.colors.status.success,
          },
          standardError: {
            backgroundColor: `rgba(220, 38, 38, ${designTokens.colors.opacity.hover})`,
            borderColor: designTokens.colors.status.error,
          },
          standardWarning: {
            backgroundColor: `rgba(245, 158, 11, ${designTokens.colors.opacity.hover})`,
            borderColor: designTokens.colors.status.warning,
          },
          standardInfo: {
            backgroundColor: `rgba(59, 130, 246, ${designTokens.colors.opacity.hover})`,
            borderColor: designTokens.colors.status.info,
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: designTokens.colors.neutral.border,
            margin: `${designTokens.spacing.md} 0`,
          },
        },
      },

      /* Tooltips render through a portal onto document.body, which sits OUTSIDE
         the #app-a11y-root wrapper carrying the product's `uiScale` zoom. So a
         14px tooltip drew at a true 14px while the app's own 13px body text
         drew at 13 x 0.92 — the tooltip was ~17% larger than everything around
         it, which is what made it read as an oversized slab. Multiplying by
         uiScale here puts it back in proportion; 13.5 is the base size, trimmed
         from 14 so it also sits a touch lighter than body copy.

         The old near-black `neutral.ink` on a light UI was heavy-handed too. A
         translucent navy with a little blur separates from the page without
         punching a hole in it. Weight drops 500 -> 400 because light-on-dark
         text optically bolds: at 500 it genuinely looked semibold. */
      MuiTooltip: {
        defaultProps: { enterDelay: 200, leaveDelay: 40 },
        styleOverrides: {
          tooltip: {
            backgroundColor: 'rgba(13, 27, 48, 0.95)',
            backdropFilter: 'blur(8px) saturate(120%)',
            WebkitBackdropFilter: 'blur(8px) saturate(120%)',
            color: 'rgba(255,255,255,0.94)',
            fontFamily: designTokens.typography.fontFamily.base,
            fontSize: `${+(13.5 * (designTokens.uiScale / 100)).toFixed(2)}px`,
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.005em',
            borderRadius: 9,
            padding: '7px 11px',
            maxWidth: 280,
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow: '0 8px 26px rgba(8, 20, 40, 0.30), 0 2px 6px rgba(8, 20, 40, 0.18)',
          },
          // Tooltips using `arrow` must match, or the arrow reads as a stray chip.
          arrow: {
            color: 'rgba(13, 27, 48, 0.95)',
            '&::before': { border: '1px solid rgba(255,255,255,0.10)' },
          },
        },
      },

      MuiModal: {
        styleOverrides: {
          backdrop: {
            backgroundColor: `rgba(15, 23, 42, ${designTokens.colors.opacity.overlay})`,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation0: {
            boxShadow: 'none',
          },
          elevation1: {
            boxShadow: designTokens.shadows.base,
          },
          elevation2: {
            boxShadow: designTokens.shadows.hover,
          },
          elevation3: {
            boxShadow: designTokens.shadows.focus,
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: designTokens.shadows.subtle,
            backgroundColor: designTokens.colors.neutral.white,
            color: designTokens.colors.neutral.ink,
          },
        },
      },

      MuiLink: {
        defaultProps: {
          underline: 'hover',
        },
        styleOverrides: {
          root: {
            color: designTokens.colors.primary.blue,
            cursor: 'pointer',
            transition: designTokens.transitions.color,
            '&:hover': {
              color: designTokens.colors.primary.dark,
            },
            '&:focus-visible': {
              outline: `2px solid ${designTokens.colors.primary.blue}`,
              outlineOffset: '2px',
              borderRadius: '2px',
            },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            color: designTokens.colors.neutral.text,
            transition: designTokens.transitions.default,
            '&:hover': {
              backgroundColor: `rgba(21, 88, 192, ${designTokens.colors.opacity.hover})`,
              color: designTokens.colors.primary.blue,
            },
            '&:focus-visible': {
              outline: `2px solid ${designTokens.colors.primary.blue}`,
              outlineOffset: '2px',
            },
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            transition: designTokens.transitions.default,
            '&:hover': {
              backgroundColor: `rgba(21, 88, 192, ${designTokens.colors.opacity.hover})`,
            },
            '&.Mui-selected': {
              backgroundColor: `rgba(21, 88, 192, ${designTokens.colors.opacity.focus})`,
              color: designTokens.colors.primary.blue,
              fontWeight: 600,
            },
          },
        },
      },

      // Select / Autocomplete dropdown panels — same premium panel language
      // (rounded corners, soft shadow, hover + selected tint) as the applicant
      // form's custom dropdown, so every <Select>/<TextField select> in the
      // app (Register, filters, etc.) looks consistent with the form.
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: designTokens.borderRadius.md,
            boxShadow: designTokens.shadows.hover,
            border: `1px solid ${designTokens.colors.neutral.border}`,
            marginTop: 6,
          },
          list: {
            padding: 6,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.sm,
            fontSize: designTokens.typography.fontSize.sm,
            padding: `${designTokens.spacing.sm} ${designTokens.spacing.sm}`,
            '&:hover': {
              backgroundColor: `rgba(21, 88, 192, ${designTokens.colors.opacity.hover})`,
            },
            '&.Mui-selected': {
              backgroundColor: `rgba(21, 88, 192, ${designTokens.colors.opacity.focus})`,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: `rgba(21, 88, 192, ${designTokens.colors.opacity.focus})`,
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontFamily: designTokens.typography.fontFamily.base,
          },
        },
      },
    },

    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 1024,
        lg: 1280,
        xl: 1536,
      },
    },
  });
};

/**
 * Theme Provider Component
 * Wraps the application with MUI theme
 */
export const AppThemeProvider = ({ children }) => {
  // Forced light mode — the app has no dark-theme styling anywhere else, so
  // following the OS dark-mode preference here would break every page.
  const theme = useMemo(() => createAppTheme('light'), []);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};

export default AppThemeProvider;
